import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { filter, take } from 'rxjs/operators';
import { WebsocketService } from '../../services/websocket.service';
import { TranslatePipe } from '@ngx-translate/core';

/**
 * Rules for one Zigbee sensor.
 *
 * A rule lives in the ZIGBEE section of the config envelope, exactly like an input's
 * events do — so it reaches the controller through the same "Upload to controller" as
 * everything else, and the same banner tells the user it has not been applied yet. There
 * is deliberately no separate "send rules" button: two ways to deliver the same thing is
 * how they drift apart.
 */
// `name` holds a translation key; the template pipes it.
const EVENTS = [
  { code: 1, name: 'zigbee.trigger.leak' },
  { code: 2, name: 'zigbee.trigger.no_leak' },
  { code: 3, name: 'zigbee.trigger.motion' },
  { code: 4, name: 'zigbee.trigger.no_motion' },
  { code: 7, name: 'zigbee.trigger.temp_above' },
  { code: 8, name: 'zigbee.trigger.temp_below' },
  { code: 9, name: 'zigbee.trigger.hum_above' },
  { code: 10, name: 'zigbee.trigger.hum_below' },
];

@Component({
  selector: 'app-zigbee-edit',
  templateUrl: './zigbee-edit.component.html',
  styleUrls: ['./zigbee-edit.component.scss'],
  standalone: true,
  imports: [TranslatePipe, CommonModule, FormsModule],
})
export class ZigbeeEditComponent implements OnInit {
  ieee = '';
  readonly typeNames: Record<number, string> = {
    0: 'zigbee.type.unknown', 1: 'zigbee.type.leak', 2: 'zigbee.type.climate', 3: 'zigbee.type.motion',
  };
  events_log: any[] = [];
  confirmDelete = false;
  notifyTempOn = false;
  notifyHumOn = false;
  notifyTempValue = 30;
  notifyHumValue = 70;
  controllerMac: string | null = null;
  device: any = null;

  outputs: any[] = [];
  scenarios: any[] = [];

  /** Rules of THIS sensor, in UI form (threshold in °C / %). */
  rules: any[] = [];
  /** Everything else the controller has — kept as-is, since a save replaces the whole table. */
  private otherRules: any[] = [];

  saved = false;

  /**
   * Only the events this sensor can produce. A leak sensor has exactly two (leak / no
   * leak): offering it "temperature above" invites a rule that can never fire.
   */
  get events() {
    switch (this.device?.type) {
      case 1: return EVENTS.filter(e => e.code === 1 || e.code === 2);      // leak
      case 3: return EVENTS.filter(e => e.code === 3 || e.code === 4);      // motion
      case 2: return EVENTS.filter(e => e.code >= 7);                        // temp / humidity
      default: return EVENTS;                                                // type not known yet
    }
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private websocketService: WebsocketService,
  ) {}

  ngOnInit(): void {
    this.ieee = this.route.snapshot.paramMap.get('ieee') ?? '';
    this.controllerMac = (window.history?.state as any)?.controllerMac ?? null;

    this.websocketService.on('ZIGBEEDEVICES').pipe(take(1)).subscribe((devices: any) => {
      this.device = (devices || []).find((d: any) => d.ieee === this.ieee) ?? null;
      const nt = this.device?.notify?.tempAbove;
      const nh = this.device?.notify?.humAbove;
      this.notifyTempOn = nt !== null && nt !== undefined;
      this.notifyHumOn = nh !== null && nh !== undefined;
      if (this.notifyTempOn) this.notifyTempValue = nt;
      if (this.notifyHumOn) this.notifyHumValue = nh;
      if (this.device && !this.device.notify) this.device.notify = { leak: false };
    });
    // History of discrete events (leak / no leak): for a leak sensor, WHEN it went off is
    // the whole point — the current state alone tells the user nothing.
    this.websocketService.on('ZIGBEEEVENTS').pipe(take(1)).subscribe((payload: any) => {
      if (payload?.ieee === this.ieee) this.events_log = payload.events || [];
    });

    this.websocketService.on('ZIGBEERULES').pipe(take(1)).subscribe((rules: any) => {
      const all = Array.isArray(rules) ? rules : [];
      this.rules = all.filter(r => r.ieee === this.ieee).map(r => this.toUi(r));
      this.otherRules = all.filter(r => r.ieee !== this.ieee);
    });

    this.websocketService.getUserDevices().pipe(
      filter((devices: any) => devices != null),
      take(1),
    ).subscribe((devices: any) => {
      const current = devices.find((d: any) => d.mac === this.controllerMac);
      if (!current) return;

      this.scenarios = current.config?.scenarios ?? [];
      // Outputs of every controller in the group: a Zigbee rule can drive a relay on a
      // neighbouring node over RS485, just like an input's action can.
      this.outputs = devices
        .filter((d: any) => d.groupID === current.groupID || d.mac === current.mac)
        .flatMap((d: any) => d.config?.outputs ?? []);
    });

    if (this.controllerMac) {
      this.websocketService.sendMessage({ type: 'ZIGBEEDEVICES', payload: { mac: this.controllerMac } });
      this.websocketService.sendMessage({ type: 'ZIGBEERULES', payload: { mac: this.controllerMac } });
      this.websocketService.sendMessage({
        type: 'ZIGBEEEVENTS',
        payload: { mac: this.controllerMac, ieee: this.ieee, limit: 50 },
      });
    }
  }

  /** Thresholds travel as hundredths (2550 = 25.5 °C). The user sees degrees. */
  private toUi(r: any) {
    return {
      event: r.event,
      threshold: (r.threshold ?? 0) / 100,
      outputEid: this.eidOf(r.action),
      actionType: r.action?.action ?? 'on',
      durationSec: r.durationSec ?? 0,
      enabled: r.enabled !== false,
    };
  }

  private eidOf(action: any): string {
    if (!action?.node) return '';
    return `${action.node}-o-${action.outputId ?? 0}`;
  }

  private toWire(r: any) {
    const eid: string = r.outputEid || '';
    return {
      ieee: this.ieee,
      event: Number(r.event),
      threshold: Math.round(Number(r.threshold || 0) * 100),
      action: {
        action: r.actionType,
        node: eid.length >= 12 ? eid.substring(0, 12) : this.controllerMac,
        outputId: eid.length >= 16 ? Number(eid.substring(15)) : 0,
      },
      durationSec: Number(r.durationSec || 0),
      enabled: !!r.enabled,
    };
  }

  get typeName(): string {
    return this.typeNames[this.device?.type] ?? 'zigbee.type.unknown';
  }

  needsThreshold(rule: any): boolean {
    return [7, 8, 9, 10].includes(Number(rule.event));
  }

  thresholdUnit(rule: any): string {
    return [7, 8].includes(Number(rule.event)) ? '°C' : '%';
  }

  addRule(): void {
    const first = this.events[0]?.code ?? 1;
    this.rules.push({
      event: first,
      threshold: 0,
      outputEid: this.outputs[0]?.eid ?? '',
      actionType: 'on',
      durationSec: 0,
      enabled: true,
    });
  }

  removeRule(i: number): void {
    this.rules.splice(i, 1);
  }

  save(): void {
    if (!this.controllerMac) return;
    const rules = [...this.otherRules, ...this.rules.map(r => this.toWire(r))];
    this.websocketService.sendMessage({
      type: 'ZIGBEEUPDATERULES',
      payload: { mac: this.controllerMac, rules },
    });
    this.saved = true;
    this.back();   // same as the input editor: saving takes you back to the controller
  }

  saveName(): void {
    this.saveDevice();
  }

  // Notifications go to the gateway owner's Telegram. Leak sensors get on/off alerts; a
  // temp/humidity sensor gets an alert when it crosses the threshold.
  saveDevice(): void {
    if (!this.device?.uuid) return;
    this.websocketService.sendMessage({
      type: 'ZIGBEEUPDATEDEVICE',
      payload: {
        mac: this.controllerMac,
        uuid: this.device.uuid,
        name: this.device.name,
        description: this.device.description,
        notify: {
          leak: !!this.device.notify?.leak,
          tempAbove: this.notifyTempOn ? Number(this.notifyTempValue) : null,
          humAbove: this.notifyHumOn ? Number(this.notifyHumValue) : null,
        },
      },
    });
  }

  deleteDevice(): void {
    if (!this.controllerMac) return;
    this.confirmDelete = false;
    this.websocketService.sendMessage({
      type: 'ZIGBEEDELETE',
      payload: { mac: this.controllerMac, ieee: this.ieee },
    });
    this.back();
  }

  eventName(e: string): string {
    return e ? 'zigbee.event.' + e.toLowerCase() : '';
  }

  isAlarm(e: string): boolean {
    return e === 'LEAK' || e === 'MOTION';
  }

  back(): void {
    if (this.controllerMac) this.router.navigate(['/controller', this.controllerMac], { state: { activeTab: 'zigbee' } });
    else this.router.navigate(['/']);
  }
}
